import { Skeleton } from '@art-of-hospitality-solutions-llc/ui-kit/components/skeleton';
import Breadcrumbs from '@Components/Breadcrumbs';
import { useParams } from 'react-router-dom';
import { useAgentProfile } from '@Hooks/useAgentProfile';
import { OPERATION_META } from '@Constants/operations';
import AgentPersonaHeader from './AgentPersonaHeader';
import AgentGlancePanel from './AgentGlancePanel';
import OperationBlock from './OperationBlock';
import ShiftReportOperation from './ShiftReportOperation';
import EventReportOperation from './EventReportOperation';
import WeddingReportOperation from './WeddingReportOperation';
import TourReportOperation from './TourReportOperation';
import HotelReportOperation from './HotelReportOperation';
import LapseDetectionOperation from './LapseDetectionOperation';
import type { ComponentType } from 'react';

// ─── Operation registry ────────────────────────────────────────────────────────
// To add a new operation: create a self-contained <XOperation soulId={...} />
// component and register it here. The desk renders whichever operations the
// agent owns — nothing else needs to change.
const OPERATION_COMPONENTS: Record<string, ComponentType<{ soulId: string }>> = {
  SHIFT_REPORT: ShiftReportOperation,
  EVENT_REPORT: EventReportOperation,
  WEDDING_REPORT: WeddingReportOperation,
  TOUR_REPORT: TourReportOperation,
  HOTEL_REPORT: HotelReportOperation,
  LAPSE_DETECTION: LapseDetectionOperation,
};

export default function AgentDesk() {
  const { soulId } = useParams();
  const { data: profile, status } = useAgentProfile({ soulId });

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'AI', href: '/agents' },
          { name: 'Agents', href: '/agents' },
          { name: profile?.name ?? 'Agent', href: `/agents/${soulId}` },
        ]}
      />

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="space-y-8 lg:col-span-2">
          {status === 'pending' || !profile ? (
            <Skeleton className="h-36 w-full rounded-xl" />
          ) : (
            <AgentPersonaHeader profile={profile} />
          )}

          {profile?.operations.map((opKey) => {
            const Component = OPERATION_COMPONENTS[opKey];
            const meta = OPERATION_META[opKey];
            if (!Component || !meta || !soulId) return null;
            return (
              <OperationBlock
                key={opKey}
                title={meta.label}
                soulId={soulId}
                actions={meta.actions}
              >
                <Component soulId={soulId} />
              </OperationBlock>
            );
          })}
        </div>

        <aside className="lg:col-span-1">
          {status !== 'pending' && profile && soulId && (
            <AgentGlancePanel soulId={soulId} profile={profile} />
          )}
        </aside>
      </div>
    </div>
  );
}
